import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { CageActiveFishGroupController_updateActiveFishGroup } from '@/services/services';

import { apiErrorStatus } from '../shared/errors';
import type { MutationState } from '../shared/mutation';
import { sendWireBody } from '../shared/wireRequest';

import { invalidateClientOverviewQueries, invalidateFishGroupQueries } from './_invalidateQueries';
import type { UpdateCageActiveFishGroupRequest } from './types';

interface ActiveFishGroupUpdate {
    cageId: number;
    fishGroupId: number;
    requestBody: UpdateCageActiveFishGroupRequest;
}

function updateCageActiveFishGroup({ cageId, fishGroupId, requestBody }: ActiveFishGroupUpdate) {
    return sendWireBody(
        'patchRequest',
        CageActiveFishGroupController_updateActiveFishGroup,
        { cageId },
        { ...requestBody, fishGroupId }
    );
}

/**
 * The save was refused because another fish group occupies the cage now: the form was
 * written for the group it showed, and the backend never applies it to a different one.
 */
export function isActiveFishGroupChangedError(error: unknown): boolean {
    return apiErrorStatus(error) === 409;
}

export interface UseUpdateCageActiveFishGroupResult extends MutationState {
    updateActiveFishGroup: (requestBody: UpdateCageActiveFishGroupRequest) => Promise<void>;
}

/**
 * Correct the name or initial population size of the fish group in a cage, from its settings.
 *
 * The same correction as `useUpdateFishGroupMetadata`, reached through the cage so a location
 * admin may make it: send only the edited fields, plus the reason. `fishGroupId` is the group the
 * form shows; the backend refuses the save when the cage holds another one by then. The client
 * overview is refetched too, since it names the pen's group in the cage header.
 */
export const useUpdateCageActiveFishGroup = (
    cageId: number,
    clientId: number,
    fishGroupId: number
): UseUpdateCageActiveFishGroupResult => {
    const queryClient = useQueryClient();
    const mutation = useMutation({ mutationFn: updateCageActiveFishGroup });
    // The save is not done when the PATCH answers: the page still shows the old name until the
    // refetches below land, so the pending state lasts until they have.
    const [isRefreshing, setIsRefreshing] = useState(false);

    const updateActiveFishGroup = async (
        requestBody: UpdateCageActiveFishGroupRequest
    ): Promise<void> => {
        await mutation.mutateAsync({ cageId, fishGroupId, requestBody });
        setIsRefreshing(true);
        try {
            // The fish-group refresh covers this cage's active-group query too.
            await Promise.all([
                invalidateFishGroupQueries(queryClient, clientId, fishGroupId),
                invalidateClientOverviewQueries(queryClient, clientId),
            ]);
        } finally {
            setIsRefreshing(false);
        }
    };

    return {
        updateActiveFishGroup,
        isLoading: mutation.isPending || isRefreshing,
        error: mutation.error,
        reset: mutation.reset,
    };
};
